/**
 * `/platform/workspaces`, every workspace in this ELASO Instance (TECH-3244).
 *
 * A thin wrapper. The panel is shared with `/organization`
 * (`components/workspaces/workspace-admin-panel.tsx`), and all this file owns is
 * which procedures answer it and when the list is refetched. The two surfaces
 * differ only in that a workspace here has to be given a tenant, and that each
 * row says which tenant it is in.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ulid } from "ulid";
import { WorkspaceAdminPanel } from "@/components/workspaces/workspace-admin-panel";
import {
	type OrganizationOption,
	useWorkspaceList,
} from "@/hooks/use-workspace-list";
import { ws } from "@/lib/websocket-wrapper";

export const Route = createFileRoute("/platform/workspaces")({
	component: PlatformWorkspacesPage,
});

function PlatformWorkspacesPage() {
	const [organizations, setOrganizations] = useState<OrganizationOption[]>([]);
	// `includeDisabled` because this is the screen that switches a workspace back
	// on, and it cannot offer that for a row it never showed.
	const { workspaces, loading, load } = useWorkspaceList(
		"instanceAdmin.listWorkspaces",
		{ includeDisabled: true }
	);

	useEffect(() => {
		load();
		// Enabled organizations only. A disabled one is refused as a create target
		// server-side, so offering it in the picker would only produce a rejection.
		ws.sendWithPromise<{ organizations: OrganizationOption[] }>({
			id: ulid(),
			procedure: "instanceAdmin.listOrganizations",
			data: { includeDisabled: false },
		})
			.then((res) => setOrganizations(res.organizations))
			.catch((err: Error) => {
				toast.error(`Failed to load organizations: ${err.message}`);
			});
	}, []);

	async function handleCreate(name: string, organizationId?: string) {
		if (!organizationId) return;
		try {
			await ws.sendWithPromise({
				id: ulid(),
				procedure: "instanceAdmin.createWorkspace",
				data: { organizationId, name },
			});
			toast.success(`Created workspace ${name}`);
			load();
		} catch (err) {
			const message = err instanceof Error ? err.message : String(err);
			toast.error(`Create failed: ${message}`);
			throw err;
		}
	}

	async function handleRename(workspaceId: string, name: string) {
		try {
			await ws.sendWithPromise({
				id: ulid(),
				procedure: "instanceAdmin.renameWorkspace",
				data: { workspaceId, name },
			});
			load();
		} catch (err) {
			const message = err instanceof Error ? err.message : String(err);
			toast.error(`Rename failed: ${message}`);
			throw err;
		}
	}

	async function handleSetEnabled(workspaceId: string, enabled: boolean) {
		try {
			await ws.sendWithPromise({
				id: ulid(),
				procedure: "instanceAdmin.setWorkspaceEnabled",
				data: { workspaceId, enabled },
			});
			load();
		} catch (err) {
			const message = err instanceof Error ? err.message : String(err);
			toast.error(`Update failed: ${message}`);
			throw err;
		}
	}

	async function handleSetExternalRef(
		workspaceId: string,
		externalRef: string | null
	) {
		try {
			await ws.sendWithPromise({
				id: ulid(),
				procedure: "instanceAdmin.setWorkspaceExternalRef",
				data: { workspaceId, externalRef },
			});
			load();
		} catch (err) {
			const message = err instanceof Error ? err.message : String(err);
			toast.error(`Update failed: ${message}`);
			throw err;
		}
	}

	// Only `/platform` wires this. `instanceAdmin.reseedWorkspace` is platform-admin
	// only, so `/organization` leaves the prop off and the control does not render
	// there.
	async function handleReseed(workspaceId: string) {
		try {
			await ws.sendWithPromise({
				id: ulid(),
				procedure: "instanceAdmin.reseedWorkspace",
				data: { workspaceId },
			});
			toast.success("Workspace repaired");
			// Refetched because a repair can change what the row says about itself,
			// and because seeing the list come back is the confirmation that it
			// worked.
			load();
		} catch (err) {
			const message = err instanceof Error ? err.message : String(err);
			toast.error(`Repair failed: ${message}`);
			throw err;
		}
	}

	return (
		<div className="flex flex-col items-center gap-8 p-8">
			<WorkspaceAdminPanel
				loading={loading}
				onCreate={handleCreate}
				onRename={handleRename}
				onReseed={handleReseed}
				onSetEnabled={handleSetEnabled}
				onSetExternalRef={handleSetExternalRef}
				organizations={organizations}
				showOrganization
				workspaces={workspaces}
			/>
		</div>
	);
}
